import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Settings, AlertTriangle, CheckCircle, Bot } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const SystemSettings = () => {
  const [autoDisableToggle, setAutoDisableToggle] = useState(true);
  const { toast } = useToast();

  const handleAutoDisableChange = (checked: boolean) => {
    setAutoDisableToggle(checked);

    if (!checked) {
      toast({
        title: "Atenção",
        description:
          "É recomendado manter a auto-desativação habilitada para melhor controle das conversas.",
        variant: "destructive",
      });
    } else {
      toast({
        title: "Configuração atualizada",
        description: "Auto-desativação das conversas foi habilitada.",
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Settings className="h-8 w-8" />
          Configurações do Sistema
        </h1>
        <p className="text-gray-600 mt-2">
          Gerencie as configurações globais do sistema
        </p>
      </div>

      {/* Configurações de Chat */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            Configurações de Chat
          </CardTitle>
          <CardDescription>
            Configure o comportamento automático das conversas
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-disable" className="text-base font-medium">
                Auto-desativação de Conversas
              </Label>
              <p className="text-sm text-muted-foreground">
                Desativa automaticamente o controle manual das conversas após um
                período de inatividade
              </p>
            </div>
            <Switch
              id="auto-disable"
              checked={autoDisableToggle}
              onCheckedChange={handleAutoDisableChange}
            />
          </div>

          {/* Alert baseado no estado do toggle */}
          {autoDisableToggle ? (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                A auto-desativação está habilitada. As conversas em modo manual
                serão automaticamente retornadas para o modo IA após períodos de
                inatividade, garantindo que nenhum cliente fique sem resposta.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Atenção:</strong> A auto-desativação está desabilitada.
                É recomendado manter esta função ativa para evitar que conversas
                em modo manual fiquem sem resposta por longos períodos.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Outras Configurações */}
      <Card>
        <CardHeader>
          <CardTitle>Outras Configurações</CardTitle>
          <CardDescription>Configurações adicionais do sistema</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="notifications" className="text-base font-medium">
                Notificações em Tempo Real
              </Label>
              <p className="text-sm text-muted-foreground">
                Receba notificações quando novas mensagens chegarem
              </p>
            </div>
            <Switch id="notifications" defaultChecked />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="sound" className="text-base font-medium">
                Alertas Sonoros
              </Label>
              <p className="text-sm text-muted-foreground">
                Reproduzir som quando novas mensagens chegarem
              </p>
            </div>
            <Switch id="sound" defaultChecked />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-archive" className="text-base font-medium">
                Arquivamento Automático
              </Label>
              <p className="text-sm text-muted-foreground">
                Arquivar conversas inativas automaticamente após 30 dias
              </p>
            </div>
            <Switch id="auto-archive" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemSettings;
